import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { augmentRelMissingEndpoint } from "../utils/enrichmentMissingEndpoint";
import EnrichmentContextNotes from "./EnrichmentContextNotes";
import { emitGuideEvent } from "../v2/guide/guideEvents";
import { useOptionalProductGuide } from "../v2/guide/GuideContext";
import { revealGuideAttentionTarget } from "../v2/utils/scrollShell";
import {
  effectiveCatalogSurfaceType,
  isEnrichmentAnchorEntity,
  resolutionKey,
  rosterTypeOverrideKeyForGap,
} from "../utils/enrichmentEntityRoster";
import {
  catalogEndpointTypeParts,
  catalogStubKey,
  catalogTypeAlignmentHint,
  catalogTypeAlignmentMessage,
  catalogUiState,
  compoundEntityNameWarning,
  compactRelationshipClaim,
  countReviewQueueNewFindings,
  filterAcceptableAiRecommendationItems,
  filterReviewQueue,
  formatCatalogEntityTypeLabel,
  formatReviewOverviewContent,
  formatReviewQueueFindingsSummary,
  getReviewCardActionLabels,
  interfaceCatalogEndpoints,
  nameMatchGuidanceCopy,
  relationshipEndpointTypeParts,
  renameReviewItemEntity,
  reviewItemEditableEntities,
  resolutionCandidateMatchesFor,
  relationshipUiState,
  reviewPrimaryActionAllowedWhenBlocked,
} from "../utils/enrichmentReviewQueue";
import { ENRICHMENT_DESCRIPTION_SAVE_HINT } from "../utils/enrichmentReviewSaveStatus";

function reviewKindBadgeClass(isInlineLight, { isCatalogRecommendation = false } = {}) {
  if (isCatalogRecommendation) {
    return isInlineLight
      ? "inline-flex items-center rounded-md bg-teal-100 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-teal-800 ring-1 ring-teal-300/80"
      : "inline-flex items-center rounded-md bg-teal-950/70 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-teal-200 ring-1 ring-teal-500/60";
  }
  return isInlineLight
    ? "inline-flex items-center rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-slate-600 ring-1 ring-slate-200/80"
    : "inline-flex items-center rounded-md bg-slate-800/80 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-slate-300 ring-1 ring-slate-600/80";
}

function CatalogTypeAlignmentNote({ hint, isInlineLight, className = "" }) {
  if (!hint) return null;
  const message = catalogTypeAlignmentMessage(hint);
  if (!message) return null;
  return (
    <p
      className={
        isInlineLight
          ? `text-[11px] leading-relaxed text-sky-800 bg-sky-50 border border-sky-200 rounded-md px-2 py-1.5 ${className}`.trim()
          : `text-[11px] leading-relaxed text-sky-100 bg-sky-950/40 border border-sky-600/40 rounded-md px-2 py-1.5 ${className}`.trim()
      }
    >
      {message}
    </p>
  );
}

function EndpointTypeFootnote({ parts, isInlineLight, className = "" }) {
  if (!Array.isArray(parts) || !parts.length) return null;
  return (
    <p
      className={
        isInlineLight
          ? `text-[11px] leading-relaxed text-slate-500 flex flex-wrap gap-x-4 gap-y-0.5 ${className}`.trim()
          : `text-[11px] leading-relaxed text-slate-400 flex flex-wrap gap-x-4 gap-y-0.5 ${className}`.trim()
      }
    >
      {parts.map(({ name, type, roleLabel }) => (
        <span key={`${roleLabel || ""}|${name}|${type}`}>
          {roleLabel ? (
            <span className="opacity-75">{roleLabel}: </span>
          ) : null}
          <span className={isInlineLight ? "text-slate-700" : "text-slate-300"}>{name}</span>
          <span className="opacity-75"> · {formatCatalogEntityTypeLabel(type)}</span>
        </span>
      ))}
    </p>
  );
}

function InterfaceEndpointsSummary({ row, preview, reviewEntity, isInlineLight }) {
  const ends = interfaceCatalogEndpoints(row, preview, reviewEntity);
  if (!ends?.source && !ends?.target) return null;
  const rowClass = isInlineLight
    ? "flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm text-slate-800"
    : "flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm text-slate-100";
  const labelClass = isInlineLight
    ? "text-xs font-medium uppercase tracking-wide text-slate-500 min-w-[3.5rem]"
    : "text-xs font-medium uppercase tracking-wide text-slate-400 min-w-[3.5rem]";
  const typeClass = isInlineLight ? "text-xs text-slate-500" : "text-xs text-slate-400";

  return (
    <div
      className={
        isInlineLight
          ? "rounded-lg border border-slate-200/90 bg-slate-50/90 px-3 py-2.5 space-y-1.5"
          : "rounded-lg border border-slate-700/80 bg-slate-900/40 px-3 py-2.5 space-y-1.5"
      }
      aria-label="Interface source and target"
    >
      {ends.source ? (
        <div className={rowClass}>
          <span className={labelClass}>Source</span>
          <span className="font-medium">{ends.source.name}</span>
          <span className={typeClass}>
            · {formatCatalogEntityTypeLabel(ends.source.type)}
          </span>
        </div>
      ) : null}
      {ends.target ? (
        <div className={rowClass}>
          <span className={labelClass}>Target</span>
          <span className="font-medium">{ends.target.name}</span>
          <span className={typeClass}>
            · {formatCatalogEntityTypeLabel(ends.target.type)}
          </span>
        </div>
      ) : null}
    </div>
  );
}

function reviewCardShellClass(
  isInlineLight,
  variant,
  { isCatalogRecommendation = false } = {}
) {
  const base = isInlineLight
    ? "rounded-2xl border shadow-sm"
    : "rounded-2xl border shadow-lg shadow-black/10";
  if (variant === "blocked") {
    return isInlineLight
      ? `${base} border-amber-200/90 bg-gradient-to-b from-amber-50/90 to-white`
      : `${base} border-amber-500/40 bg-gradient-to-b from-amber-950/30 to-slate-900/50`;
  }
  if (variant === "approved") {
    return isInlineLight
      ? `${base} border-emerald-200/90 bg-gradient-to-b from-emerald-50/50 to-white`
      : `${base} border-emerald-600/45 bg-gradient-to-b from-emerald-950/25 to-slate-900/50`;
  }
  if (variant === "busy") {
    return isInlineLight
      ? `${base} border-indigo-200 bg-white ring-2 ring-indigo-100`
      : `${base} border-indigo-500/45 bg-slate-900/55 ring-2 ring-indigo-500/25`;
  }
  if (isCatalogRecommendation) {
    return isInlineLight
      ? `${base} border-teal-300/90 bg-gradient-to-b from-teal-50/95 to-white ring-1 ring-teal-200/70`
      : `${base} border-teal-500/50 bg-gradient-to-b from-teal-950/40 to-slate-900/50 ring-1 ring-teal-500/30`;
  }
  return isInlineLight
    ? `${base} border-slate-200/90 bg-white`
    : `${base} border-slate-600/50 bg-slate-900/45`;
}

function reviewBtnPrimary(isInlineLight) {
  return isInlineLight
    ? "inline-flex items-center justify-center min-h-[2.25rem] px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-45 text-white text-sm font-medium shadow-sm transition-colors"
    : "inline-flex items-center justify-center min-h-[2.25rem] px-4 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-45 text-white text-sm font-medium shadow-sm transition-colors";
}

function reviewBtnSecondary(isInlineLight) {
  return isInlineLight
    ? "inline-flex items-center justify-center min-h-[2.25rem] px-4 rounded-lg border border-indigo-200 bg-indigo-50 hover:bg-indigo-100 disabled:opacity-45 text-indigo-900 text-sm font-medium transition-colors"
    : "inline-flex items-center justify-center min-h-[2.25rem] px-4 rounded-lg border border-indigo-500/45 bg-indigo-950/35 hover:bg-indigo-900/45 disabled:opacity-45 text-indigo-100 text-sm font-medium transition-colors";
}

function reviewBtnGhost(isInlineLight) {
  return isInlineLight
    ? "inline-flex items-center justify-center min-h-[2.25rem] px-4 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-45 text-slate-700 text-sm font-medium transition-colors"
    : "inline-flex items-center justify-center min-h-[2.25rem] px-4 rounded-lg border border-slate-600 bg-slate-800/60 hover:bg-slate-700/80 disabled:opacity-45 text-slate-200 text-sm font-medium transition-colors";
}

/** Add to map is always emerald; Link stays indigo; other primaries stay emerald. */
function reviewBtnForDecisionLabel(label, isInlineLight, fallback = "primary") {
  const s = String(label || "").toLowerCase();
  if (
    s.includes("add to map") ||
    s.includes("use on map") ||
    s.includes("add as new")
  ) {
    return reviewBtnPrimary(isInlineLight);
  }
  if (s.includes("link to existing")) {
    return reviewBtnSecondary(isInlineLight);
  }
  if (fallback === "secondary") return reviewBtnSecondary(isInlineLight);
  if (fallback === "ghost") return reviewBtnGhost(isInlineLight);
  return reviewBtnPrimary(isInlineLight);
}

/** Shared decision row — always Add → Link → Skip, button beside its hint. */
function ReviewDecisionFooter({
  isInlineLight,
  addLabel,
  linkLabel,
  discardLabel = "Skip",
  busy = false,
  busyLabel = "Saving…",
  addDisabled = false,
  linkDisabled = false,
  discardDisabled = false,
  onAdd,
  onLink,
  onDiscard,
  addTitle,
  linkTitle,
  addHint = null,
  linkHint = null,
  discardHint = null,
  extra = null,
  highlightAdd = false,
  addButtonRef = null,
}) {
  if (!addLabel && !linkLabel && !discardLabel) return null;
  const muted = isInlineLight
    ? "mt-1 text-[11px] leading-snug text-slate-500"
    : "mt-1 text-[11px] leading-snug text-slate-400";
  const cell = "flex min-w-0 flex-1 flex-col sm:min-w-[9rem]";
  const addClass = `${reviewBtnForDecisionLabel(addLabel, isInlineLight)}${
    highlightAdd && !addDisabled && !busy ? " example-cta-attention" : ""
  }`;

  return (
    <footer
      className={
        isInlineLight
          ? "mt-3.5 border-t border-slate-200/90 pt-3"
          : "mt-3.5 border-t border-slate-700/80 pt-3"
      }
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:gap-2.5">
        {addLabel ? (
          <div className={cell}>
            <button
              type="button"
              ref={addButtonRef || undefined}
              disabled={addDisabled}
              onClick={onAdd}
              title={addTitle}
              className={addClass}
            >
              {busy ? busyLabel : addLabel}
            </button>
            {addHint ? <p className={muted}>{addHint}</p> : null}
          </div>
        ) : null}
        {linkLabel ? (
          <div className={cell}>
            <button
              type="button"
              disabled={linkDisabled}
              onClick={onLink}
              title={linkTitle}
              className={reviewBtnForDecisionLabel(linkLabel, isInlineLight, "secondary")}
            >
              {busy ? busyLabel : linkLabel}
            </button>
            {linkHint ? <p className={muted}>{linkHint}</p> : null}
          </div>
        ) : null}
        {discardLabel ? (
          <div className={cell}>
            <button
              type="button"
              disabled={discardDisabled}
              onClick={onDiscard}
              className={reviewBtnGhost(isInlineLight)}
            >
              {discardLabel}
            </button>
            {discardHint ? <p className={muted}>{discardHint}</p> : null}
            {extra}
          </div>
        ) : null}
      </div>
    </footer>
  );
}

function reviewNavBtn(isInlineLight) {
  return isInlineLight
    ? "inline-flex items-center justify-center h-9 w-9 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40 text-slate-700 transition-colors shrink-0"
    : "inline-flex items-center justify-center h-9 w-9 rounded-lg border border-slate-600 bg-slate-800/60 hover:bg-slate-700/80 disabled:opacity-40 text-slate-200 transition-colors shrink-0";
}

function reviewWizardFooterShell(isInlineLight) {
  return isInlineLight
    ? "px-1 pt-2 pb-0 mt-0 border-t border-slate-200/90"
    : "px-1 pt-2 pb-0 mt-0 border-t border-slate-700/80";
}

function ReviewNavArrow({ direction }) {
  const isPrev = direction === "prev";
  return (
    <svg
      className="h-4 w-4"
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden
    >
      {isPrev ? (
        <path
          fillRule="evenodd"
          d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z"
          clipRule="evenodd"
        />
      ) : (
        <path
          fillRule="evenodd"
          d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
          clipRule="evenodd"
        />
      )}
    </svg>
  );
}

function candidateActionLabel(cand) {
  const name = String(cand?.name || "Catalog entity").trim();
  const approval = String(cand?.approval_status || "").trim();
  return approval ? `${name} (${approval})` : name;
}

/** Name-match suggestions: name only — approval badges (e.g. Proposed) reduce confidence. */
function candidateSuggestionName(cand) {
  return String(cand?.name || "Catalog entity").trim() || "Catalog entity";
}

function NameMatchSuggestionsModal({
  isOpen,
  isInlineLight,
  surfaceName,
  candidates = [],
  busy = false,
  onClose,
  onPickCandidate,
  onChooseAnother,
}) {
  if (!isOpen) return null;

  const name = String(surfaceName || "this name").trim() || "this name";
  const list = Array.isArray(candidates) ? candidates.slice(0, 5) : [];
  const backdropClass =
    "fixed inset-0 z-[200] flex items-center justify-center bg-black/55 p-4";
  const shellClass = isInlineLight
    ? "flex w-full max-w-md flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
    : "flex w-full max-w-md flex-col overflow-hidden rounded-2xl border border-slate-600 bg-slate-900 shadow-2xl";
  const muted = isInlineLight
    ? "text-sm leading-relaxed text-slate-600"
    : "text-sm leading-relaxed text-slate-300";
  const titleClass = isInlineLight
    ? "text-lg font-semibold text-slate-900"
    : "text-lg font-semibold text-slate-100";
  const candBtn = isInlineLight
    ? "w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-left text-sm font-medium text-slate-900 hover:border-indigo-400 disabled:opacity-40"
    : "w-full rounded-lg border border-slate-600 bg-slate-900/50 px-3 py-2.5 text-left text-sm font-medium text-slate-100 hover:border-indigo-400 disabled:opacity-40";
  const secondaryBtn = isInlineLight
    ? "px-3 py-2 rounded-lg border border-indigo-300 text-sm font-medium text-indigo-800 disabled:opacity-40"
    : "px-3 py-2 rounded-lg border border-indigo-500/45 text-sm font-medium text-indigo-200 disabled:opacity-40";
  const ghostBtn = isInlineLight
    ? "px-3 py-2 text-sm font-medium text-slate-500 disabled:opacity-40"
    : "px-3 py-2 text-sm font-medium text-slate-400 disabled:opacity-40";

  return createPortal(
    <div
      className={backdropClass}
      role="dialog"
      aria-modal="true"
      aria-labelledby="name-match-suggestions-title"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose?.();
      }}
    >
      <div className={shellClass}>
        <div
          className={
            isInlineLight
              ? "border-b border-slate-200 px-5 py-4"
              : "border-b border-slate-700 px-5 py-4"
          }
        >
          <h2 id="name-match-suggestions-title" className={titleClass}>
            Link &ldquo;{name}&rdquo; to an existing item
          </h2>
          <p className={`mt-2 ${muted}`}>
            Potential matches on your map. If you think this is a different thing,
            ignore the suggestion and choose another — or cancel.
          </p>
        </div>
        <div className="space-y-2 px-5 py-4">
          {list.length ? (
            list.map((cand) => (
              <button
                key={cand.id || candidateSuggestionName(cand)}
                type="button"
                disabled={busy}
                onClick={() => onPickCandidate?.(cand)}
                className={candBtn}
              >
                {candidateSuggestionName(cand)}
              </button>
            ))
          ) : (
            <p className={muted}>No close matches were suggested.</p>
          )}
        </div>
        <div
          className={
            isInlineLight
              ? "flex flex-wrap items-center justify-end gap-2 border-t border-slate-200 px-5 py-3"
              : "flex flex-wrap items-center justify-end gap-2 border-t border-slate-700 px-5 py-3"
          }
        >
          <button
            type="button"
            disabled={busy}
            className={ghostBtn}
            onClick={() => onClose?.()}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy}
            className={secondaryBtn}
            onClick={() => onChooseAnother?.()}
          >
            Choose another…
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function candidateMatchesForSurface(preview, directMatches, surfaceName, entityType) {
  if (Array.isArray(directMatches) && directMatches.length) return directMatches;
  return resolutionCandidateMatchesFor(preview, surfaceName, entityType);
}

function setupMatchAction(preview, rk, matchActions) {
  const live = matchActions[rk] || {};
  if (live.state === "confirming" || live.state === "error") return live;
  if (live.state) return live;
  const persisted = preview?.enrichment_review_outcomes?.resolution_matches?.[rk];
  if (persisted === "confirmed" || persisted === "kept_new" || persisted === "discarded") {
    return { state: persisted, error: "" };
  }
  return live;
}

function resolveInitialReviewPhase({ reviewComplete, queueLength }) {
  if (reviewComplete || queueLength === 0) return "done";
  return "review";
}

function ReviewProgressHeader({
  index,
  total,
  reviewed,
  isInlineLight,
  setupNeeded,
  onOpenSetup,
  onOpenOverview,
  filterMode = "all",
  onFilterModeChange,
  queueTotal = 0,
  newFindingsCount = 0,
  actionableCount = 0,
}) {
  const current = total > 0 ? index + 1 : 0;
  // total is the *remaining* queue (it shrinks as items are decided), so
  // progress must be done / (done + remaining) — not current / remaining,
  // which stays near zero until the last few items.
  const decidedCount = Math.max(0, Number(reviewed) || 0);
  const overallCount = decidedCount + Math.max(0, total);
  const progressPct =
    overallCount > 0
      ? Math.min(100, Math.round((decidedCount / overallCount) * 100))
      : 0;
  const showFilter = typeof onFilterModeChange === "function" && queueTotal > 0;

  return (
    <div
      className={
        isInlineLight
          ? "rounded-xl border border-slate-200/90 bg-slate-50/80 px-3 py-2"
          : "rounded-xl border border-slate-700/80 bg-slate-900/35 px-3 py-2"
      }
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <p
          className={
            isInlineLight
              ? "text-sm font-semibold text-slate-900 tabular-nums"
              : "text-sm font-semibold text-slate-100 tabular-nums"
          }
        >
          Item {current} of {total}
          {filterMode === "new" && queueTotal > total ? (
            <span
              className={
                isInlineLight
                  ? "ml-1.5 text-xs font-normal text-slate-500"
                  : "ml-1.5 text-xs font-normal text-slate-400"
              }
            >
              ({queueTotal} total)
            </span>
          ) : null}
        </p>
        {total > 0 ? (
          <div
            className={
              isInlineLight
                ? "h-1 min-w-[3.5rem] flex-1 overflow-hidden rounded-full bg-slate-200"
                : "h-1 min-w-[3.5rem] flex-1 overflow-hidden rounded-full bg-slate-700"
            }
            role="progressbar"
            aria-valuenow={progressPct}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`Review progress: ${decidedCount} of ${overallCount} decided`}
          >
            <div
              className={
                isInlineLight
                  ? "h-full rounded-full bg-indigo-500 transition-[width] duration-300"
                  : "h-full rounded-full bg-indigo-400 transition-[width] duration-300"
              }
              style={{ width: `${progressPct}%` }}
            />
          </div>
        ) : null}
        <div className="ml-auto flex flex-wrap items-center gap-x-3 gap-y-2">
          {reviewed > 0 ? (
            <span
              className={
                isInlineLight
                  ? "inline-flex items-center rounded-full bg-slate-200/80 px-2.5 py-0.5 text-xs font-medium text-slate-700 tabular-nums"
                  : "inline-flex items-center rounded-full bg-slate-700/80 px-2.5 py-0.5 text-xs font-medium text-slate-200 tabular-nums"
              }
            >
              {reviewed} done
            </span>
          ) : null}
          {showFilter ? (
            <div
              className={
                isInlineLight
                  ? "inline-flex rounded-lg border border-slate-200 bg-white p-0.5"
                  : "inline-flex rounded-lg border border-slate-600 bg-slate-800/80 p-0.5"
              }
              role="group"
              aria-label="Filter review queue"
            >
              <button
                type="button"
                onClick={() => onFilterModeChange("all")}
                aria-pressed={filterMode === "all"}
                className={
                  filterMode === "all"
                    ? isInlineLight
                      ? "rounded-md bg-indigo-600 px-2.5 py-1 text-[11px] font-medium text-white"
                      : "rounded-md bg-indigo-500 px-2.5 py-1 text-[11px] font-medium text-white"
                    : isInlineLight
                      ? "rounded-md px-2.5 py-1 text-[11px] font-medium text-slate-600 hover:text-slate-900"
                      : "rounded-md px-2.5 py-1 text-[11px] font-medium text-slate-300 hover:text-slate-100"
                }
              >
                All ({queueTotal})
              </button>
              <button
                type="button"
                onClick={() => onFilterModeChange("new")}
                aria-pressed={filterMode === "new"}
                disabled={actionableCount === 0}
                title={
                  actionableCount === 0
                    ? "All items already match something on your map"
                    : "Items that still need you to pick a match or add as new"
                }
                className={
                  filterMode === "new"
                    ? isInlineLight
                      ? "rounded-md bg-indigo-600 px-2.5 py-1 text-[11px] font-medium text-white disabled:opacity-50"
                      : "rounded-md bg-indigo-500 px-2.5 py-1 text-[11px] font-medium text-white disabled:opacity-50"
                    : isInlineLight
                      ? "rounded-md px-2.5 py-1 text-[11px] font-medium text-slate-600 hover:text-slate-900 disabled:opacity-40"
                      : "rounded-md px-2.5 py-1 text-[11px] font-medium text-slate-300 hover:text-slate-100 disabled:opacity-40"
                }
              >
                Action needed ({actionableCount})
              </button>
            </div>
          ) : null}
          {setupNeeded ? (
            <button
              type="button"
              onClick={onOpenSetup}
              className={
                isInlineLight
                  ? "text-xs font-medium text-indigo-700 hover:text-indigo-900 underline-offset-2 hover:underline"
                  : "text-xs font-medium text-indigo-300 hover:text-indigo-100 underline-offset-2 hover:underline"
              }
            >
              Match names
            </button>
          ) : null}
          {typeof onOpenOverview === "function" ? (
            <button
              type="button"
              onClick={onOpenOverview}
              className={
                isInlineLight
                  ? "text-xs font-medium text-indigo-700 hover:text-indigo-900 underline-offset-2 hover:underline"
                  : "text-xs font-medium text-indigo-300 hover:text-indigo-100 underline-offset-2 hover:underline"
              }
            >
              Overview
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function NameMatchReviewCard({
  item,
  isInlineLight,
  actionState,
  actionError,
  preview,
  reviewEntity,
  matchActions,
  renderRosterTypeSelect,
  onConfirmMatch,
  onOpenEntityPicker,
  onKeepNew,
  onDiscardMatch,
  onRenameEntity,
  reviewSaveLocked = false,
  /** When false, decision buttons are omitted (legacy footer path). Prefer true. */
  showDecisionActions = true,
  highlightAddToMap = false,
  addToMapButtonRef = null,
}) {
  const [matchModalOpen, setMatchModalOpen] = useState(false);
  const resRow = item?.row || {};
  const rk =
    item?.resolutionKey ||
    resolutionKey(resRow);
  const action = setupMatchAction(preview, rk, matchActions);
  const busy =
    actionState === "approving" ||
    actionState === "creating" ||
    action.state === "confirming" ||
    action.state === "creating";
  const terminal =
    actionState === "approved" ||
    actionState === "discarded" ||
    action.state === "confirmed" ||
    action.state === "kept_new" ||
    action.state === "discarded";
  const cands = Array.isArray(resRow?.candidate_matches) ? resRow.candidate_matches : [];
  const rosterKey = `resolution:${resolutionKey(resRow)}`;
  const overridesMap = preview?.enrichment_review_outcomes?.entity_type_overrides || {};
  const effectiveType =
    overridesMap[rosterKey] ?? String(resRow?.entity_type || "application").trim();
  const surfaceName = String(resRow?.surface_name || item?.claim || "").trim();
  const labels = getReviewCardActionLabels(item);
  const shellVariant = terminal
    ? actionState === "discarded" || action.state === "discarded"
      ? "default"
      : "approved"
    : busy
      ? "busy"
      : "default";
  const editableEntities = useMemo(
    () => reviewItemEditableEntities(item, preview, reviewEntity),
    [item, preview, reviewEntity]
  );

  const openEntityPicker = () => {
    onOpenEntityPicker?.({
      surfaceName: resRow?.surface_name,
      entityType: effectiveType,
      title: `Link to existing ${effectiveType} for ${resRow?.surface_name}`,
      source: "setup",
      relationshipHint: "",
      resolutionRow: {
        ...resRow,
        entity_type: effectiveType,
      },
    });
  };

  const handleLinkExisting = () => {
    if (cands.length > 0) {
      setMatchModalOpen(true);
      return;
    }
    openEntityPicker();
  };

  return (
    <article
      className={`transition-colors ${reviewCardShellClass(isInlineLight, shellVariant)} px-4 py-3.5 sm:px-5`}
    >
      <header className="flex items-start justify-between gap-3 mb-2.5">
        <span className={reviewKindBadgeClass(isInlineLight)}>{item.kindLabel || "name matching"}</span>
        {busy ? (
          <span
            className={
              isInlineLight
                ? "inline-flex items-center gap-1.5 text-xs font-medium text-indigo-700"
                : "inline-flex items-center gap-1.5 text-xs font-medium text-indigo-300"
            }
          >
            <span
              className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent"
              aria-hidden
            />
            Saving
          </span>
        ) : null}
        {!busy && terminal ? (
          <span
            className={
              actionState === "discarded" || action.state === "discarded"
                ? isInlineLight
                  ? "text-xs font-medium text-slate-500"
                  : "text-xs font-medium text-slate-400"
                : isInlineLight
                  ? "text-xs font-medium text-emerald-700"
                  : "text-xs font-medium text-emerald-300"
            }
          >
            {action.state === "confirmed" || actionState === "approved"
              ? "Linked to existing"
              : action.state === "kept_new"
                ? "Will add to map"
                : "Skipped"}
          </span>
        ) : null}
      </header>

      <div className="space-y-2">
        <EditableEntityNames
          entities={editableEntities}
          isInlineLight={isInlineLight}
          disabled={busy || terminal || reviewSaveLocked}
          onRenameEntity={(entityKey, newName) => onRenameEntity?.(item, entityKey, newName)}
        />
        {!terminal ? (
          <p
            className={
              isInlineLight
                ? "text-sm leading-relaxed text-slate-700"
                : "text-sm leading-relaxed text-slate-300"
            }
          >
            {nameMatchGuidanceCopy(surfaceName, effectiveType, cands.length > 0)}
          </p>
        ) : null}
        <CatalogTypeAlignmentNote
          hint={catalogTypeAlignmentHint(preview, surfaceName, effectiveType, resRow)}
          isInlineLight={isInlineLight}
        />
        {!terminal &&
        renderRosterTypeSelect &&
        !isEnrichmentAnchorEntity(preview, reviewEntity, {
          surfaceName: resRow.surface_name,
          entityType: resRow.entity_type,
          entityId: resRow.entity_id || resRow.linked_entity_id,
        }) ? (
          <div className="flex flex-wrap items-center gap-2">
            <span className={isInlineLight ? "text-xs text-slate-600" : "text-xs text-slate-400"}>
              Type on map:
            </span>
            {renderRosterTypeSelect(
              rosterKey,
              String(resRow.entity_type || "application").trim(),
              busy || reviewSaveLocked,
              isInlineLight,
              null,
              surfaceName
            )}
          </div>
        ) : null}
        {actionError ? <p className="text-xs text-rose-400">{actionError}</p> : null}
      </div>

      {!terminal && showDecisionActions ? (
        <ReviewDecisionFooter
          isInlineLight={isInlineLight}
          addLabel={labels.addLabel || labels.primary}
          linkLabel={labels.linkLabel || labels.secondary}
          discardLabel={labels.discard}
          busy={busy}
          busyLabel={labels.busy}
          addDisabled={busy || reviewSaveLocked}
          linkDisabled={busy || reviewSaveLocked}
          discardDisabled={busy || reviewSaveLocked}
          onAdd={() => onKeepNew({ ...resRow, entity_type: effectiveType })}
          onLink={handleLinkExisting}
          onDiscard={() => onDiscardMatch(resRow)}
          addHint={labels.primaryHint || "If it does not exist yet."}
          linkHint={
            labels.secondaryHint ||
            "If this is a duplicate already on your map. Click the button to see potential matches."
          }
          discardHint={labels.discardHint || "If you do not want it on the map."}
          highlightAdd={highlightAddToMap}
          addButtonRef={addToMapButtonRef}
        />
      ) : null}

      <NameMatchSuggestionsModal
        isOpen={matchModalOpen && !terminal}
        isInlineLight={isInlineLight}
        surfaceName={surfaceName}
        candidates={cands}
        busy={busy || reviewSaveLocked}
        onClose={() => setMatchModalOpen(false)}
        onPickCandidate={(cand) => {
          setMatchModalOpen(false);
          onConfirmMatch({ ...resRow, entity_type: effectiveType }, cand);
        }}
        onChooseAnother={() => {
          setMatchModalOpen(false);
          openEntityPicker();
        }}
      />
    </article>
  );
}

function ReviewOverviewView({
  isInlineLight,
  anchorSummary,
  counts,
  overviewContent,
  stats,
  decidedItems = [],
  anchorName = "",
  onStartReviewNew,
  onStartReviewAll,
  onBulkAcceptCatalogMatches,
  onMarkReviewComplete,
  onOpenSetup,
  setupNeeded,
  bulkBusy = false,
  actionsDisabled = false,
  saveStatusMessage = "",
  closingReview = false,
  reviewComplete = false,
  catalogMatchCount = 0,
  onReviseDecided,
}) {
  const cardShell = isInlineLight
    ? "rounded-2xl border border-slate-200/90 bg-white shadow-sm px-4 py-3.5 sm:px-5"
    : "rounded-2xl border border-slate-600/50 bg-slate-900/45 shadow-lg shadow-black/10 px-4 py-3.5 sm:px-5";

  const statCell = isInlineLight
    ? "rounded-lg border border-slate-200 bg-slate-50/80 px-3 py-2.5"
    : "rounded-lg border border-slate-700/80 bg-slate-900/35 px-3 py-2.5";

  const statLabel = isInlineLight ? "text-[11px] text-slate-500" : "text-[11px] text-slate-400";
  const statValue = isInlineLight
    ? "text-lg font-semibold text-slate-900 tabular-nums"
    : "text-lg font-semibold text-slate-100 tabular-nums";

  const disabled = actionsDisabled || bulkBusy;

  return (
    <div className="space-y-4">
      <SavingToCatalogBanner
        message={saveStatusMessage}
        isInlineLight={isInlineLight}
        closing={closingReview}
      />
      <div className={cardShell}>
        <h2
          className={
            isInlineLight
              ? "text-base font-semibold text-slate-900"
              : "text-base font-semibold text-slate-100"
          }
        >
          {overviewContent.title}
        </h2>
        <p
          className={
            isInlineLight
              ? "mt-2 text-sm leading-relaxed text-slate-700"
              : "mt-2 text-sm leading-relaxed text-slate-300"
          }
        >
          {overviewContent.body}
        </p>
        {counts.total > 0 ? (
          <dl className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
            <div className={statCell}>
              <dt className={statLabel}>Pending</dt>
              <dd className={statValue}>{counts.total}</dd>
            </div>
            <div className={statCell}>
              <dt className={statLabel}>New items</dt>
              <dd className={statValue}>{counts.newCount}</dd>
            </div>
            <div className={statCell}>
              <dt className={statLabel}>Need a match</dt>
              <dd className={statValue}>{counts.needsReviewCount}</dd>
            </div>
            <div className={statCell}>
              <dt className={statLabel}>Already on map</dt>
              <dd className={statValue}>{counts.existingMatchCount}</dd>
            </div>
          </dl>
        ) : null}
        <p
          className={
            isInlineLight ? "mt-3 text-xs text-slate-500" : "mt-3 text-xs text-slate-400"
          }
        >
          {anchorSummary}
          {stats.reviewed > 0 ? ` · ${stats.reviewed} done` : ""}
        </p>

        <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
          {counts.actionableCount > 0 ? (
            <button
              type="button"
              disabled={disabled}
              onClick={onStartReviewNew}
              className={reviewBtnPrimary(isInlineLight)}
            >
              Review items needing action ({counts.actionableCount})
            </button>
          ) : null}
          {counts.total > 0 ? (
            <button
              type="button"
              disabled={disabled}
              onClick={onStartReviewAll}
              className={
                counts.actionableCount > 0
                  ? reviewBtnSecondary(isInlineLight)
                  : reviewBtnPrimary(isInlineLight)
              }
            >
              Review all ({counts.total})
            </button>
          ) : null}
          {catalogMatchCount > 0 && onBulkAcceptCatalogMatches ? (
            <button
              type="button"
              disabled={disabled}
              onClick={onBulkAcceptCatalogMatches}
              className={reviewBtnGhost(isInlineLight)}
            >
              {bulkBusy
                ? "Accepting recommendations…"
                : `Accept all AI recommendations (${catalogMatchCount})`}
            </button>
          ) : null}
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
          {setupNeeded ? (
            <button
              type="button"
              disabled={disabled}
              onClick={onOpenSetup}
              className={
                isInlineLight
                  ? "text-xs font-medium text-indigo-700 hover:text-indigo-900 underline disabled:opacity-40"
                  : "text-xs font-medium text-indigo-300 hover:text-indigo-100 underline disabled:opacity-40"
              }
            >
              Back to name matching
            </button>
          ) : null}
        </div>
      </div>

      <DecidedItemsSection
        items={decidedItems}
        isInlineLight={isInlineLight}
        anchorName={anchorName}
        defaultExpanded={false}
        onReviseDecided={onReviseDecided}
        reviseDisabled={actionsDisabled}
      />
    </div>
  );
}

function catalogEndpointSuggestedType(row, role) {
  if (role === "provider") {
    return String(
      row?.provider_entity_type || row?.missing_entity_type || "application"
    ).trim();
  }
  return String(
    row?.consumer_entity_type || row?.missing_entity_type || "application"
  ).trim();
}

function CatalogEndpointHints({
  row,
  preview,
  reviewEntity,
  isInlineLight,
  renderRosterTypeSelect,
  onUseCandidate,
  onOpenEntityPicker,
}) {
  const prov = row.provider_application_name;
  const cons = row.consumer_application_name;
  const provType = catalogEndpointSuggestedType(row, "provider");
  const consType = catalogEndpointSuggestedType(row, "consumer");
  const provMissing = !String(row?.provider_entity_id || "").trim();
  const consMissing = !String(row?.consumer_entity_id || "").trim();
  const effectiveProvType = effectiveCatalogSurfaceType(
    preview,
    prov,
    provType,
    reviewEntity
  );
  const effectiveConsType = effectiveCatalogSurfaceType(
    preview,
    cons,
    consType,
    reviewEntity
  );
  const providerCandidates = candidateMatchesForSurface(
    preview,
    row?.provider_candidate_matches,
    prov,
    effectiveProvType
  );
  const consumerCandidates = candidateMatchesForSurface(
    preview,
    row?.consumer_candidate_matches,
    cons,
    effectiveConsType
  );

  return (
    <div className="mt-2 space-y-2">
      {provMissing && prov ? (
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs opacity-80">Source:</span>
            {renderRosterTypeSelect(
              rosterTypeOverrideKeyForGap(preview, prov, provType, reviewEntity),
              provType,
              false,
              isInlineLight,
              null,
              prov
            )}
            {providerCandidates.slice(0, 3).map((cand) => (
              <button
                key={cand.id}
                type="button"
                onClick={() => onUseCandidate(cand, prov, effectiveProvType)}
                className="px-2 py-0.5 rounded bg-indigo-700 text-white text-xs"
              >
                Link — {candidateActionLabel(cand)}
              </button>
            ))}
            <button
              type="button"
              onClick={() =>
                onOpenEntityPicker?.({
                  surfaceName: prov,
                  entityType: effectiveProvType,
                  title: `Link to existing ${effectiveProvType} for ${prov}`,
                  source: "catalog_provider_gap",
                  relationshipHint: String(
                    row?.suggested_name || row?.interface_name || "data flow"
                  ).replace(/_/g, " "),
                })
              }
              className={
                isInlineLight
                  ? "px-2 py-0.5 rounded border border-indigo-300 text-xs text-indigo-800"
                  : "px-2 py-0.5 rounded border border-indigo-500/45 text-xs text-indigo-200"
              }
            >
              Link to existing…
            </button>
          </div>
          <CatalogTypeAlignmentNote
            hint={catalogTypeAlignmentHint(preview, prov, effectiveProvType)}
            isInlineLight={isInlineLight}
          />
        </div>
      ) : null}
      {consMissing && cons ? (
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs opacity-80">Target:</span>
            {renderRosterTypeSelect(
              rosterTypeOverrideKeyForGap(preview, cons, consType, reviewEntity),
              consType,
              false,
              isInlineLight,
              null,
              cons
            )}
            {consumerCandidates.slice(0, 3).map((cand) => (
              <button
                key={cand.id}
                type="button"
                onClick={() => onUseCandidate(cand, cons, effectiveConsType)}
                className="px-2 py-0.5 rounded bg-indigo-700 text-white text-xs"
              >
                Link — {candidateActionLabel(cand)}
              </button>
            ))}
            <button
              type="button"
              onClick={() =>
                onOpenEntityPicker?.({
                  surfaceName: cons,
                  entityType: effectiveConsType,
                  title: `Link to existing ${effectiveConsType} for ${cons}`,
                  source: "catalog_consumer_gap",
                  relationshipHint: String(
                    row?.suggested_name || row?.interface_name || "data flow"
                  ).replace(/_/g, " "),
                })
              }
              className={
                isInlineLight
                  ? "px-2 py-0.5 rounded border border-indigo-300 text-xs text-indigo-800"
                  : "px-2 py-0.5 rounded border border-indigo-500/45 text-xs text-indigo-200"
              }
            >
              Link to existing…
            </button>
          </div>
          <CatalogTypeAlignmentNote
            hint={catalogTypeAlignmentHint(preview, cons, effectiveConsType)}
            isInlineLight={isInlineLight}
          />
        </div>
      ) : null}
    </div>
  );
}

function MetamodelAdjustmentNote({ preview, proposedKey, isInlineLight }) {
  const adj =
    preview?.enrichment_review_outcomes?.relationship_metamodel_adjustments?.[proposedKey];
  if (!adj?.from && !adj?.pending) return null;
  if (adj?.pending) {
    return (
      <p
        className={
          isInlineLight
            ? "text-xs text-sky-800 bg-sky-50 border border-sky-200 rounded-lg px-3 py-2"
            : "text-xs text-sky-100 bg-sky-950/40 border border-sky-600/40 rounded-lg px-3 py-2"
        }
      >
        Updating connection for your map…
      </p>
    );
  }
  if (!adj?.to || adj.from === adj.to) return null;
  return (
    <p
      className={
        isInlineLight
          ? "text-xs text-sky-800 bg-sky-50 border border-sky-200 rounded-lg px-3 py-2 leading-relaxed"
          : "text-xs text-sky-100 bg-sky-950/40 border border-sky-600/40 rounded-lg px-3 py-2 leading-relaxed"
      }
    >
      Connection type adjusted:{" "}
      <span className="font-medium">{adj.from}</span>
      <span aria-hidden> → </span>
      <span className="font-medium">{adj.to}</span>
    </p>
  );
}

function MetamodelSyncBanner({ notice, isInlineLight, onDismiss }) {
  if (!notice || (!notice.adjusted && !notice.discarded)) return null;
  const parts = [];
  if (notice.adjusted) {
    parts.push(
      `${notice.adjusted} relationship suggestion${notice.adjusted === 1 ? "" : "s"} updated`
    );
  }
  if (notice.discarded) {
    parts.push(
      `${notice.discarded} removed (not allowed for the new entity type${notice.discarded === 1 ? "" : "s"})`
    );
  }
  return (
    <div
      className={
        isInlineLight
          ? "rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-900 flex flex-wrap items-start justify-between gap-2"
          : "rounded-lg border border-sky-600/40 bg-sky-950/35 px-3 py-2 text-sm text-sky-100 flex flex-wrap items-start justify-between gap-2"
      }
      role="status"
    >
      <p>{parts.join("; ")}.</p>
      {onDismiss ? (
        <button
          type="button"
          onClick={onDismiss}
          className={
            isInlineLight
              ? "text-xs font-medium text-sky-800 hover:underline shrink-0"
              : "text-xs font-medium text-sky-200 hover:underline shrink-0"
          }
        >
          Dismiss
        </button>
      ) : null}
    </div>
  );
}

function EntityTypeChangeConfirmDialog({
  confirmation,
  isInlineLight,
  onConfirm,
  onCancel,
}) {
  if (!confirmation) return null;
  const entityName = String(confirmation.entityName || "this entity").trim() || "this entity";
  const newTypeLabel = formatCatalogEntityTypeLabel(confirmation.newType);

  const shell = isInlineLight
    ? "w-full max-w-md rounded-2xl border border-amber-200 bg-white shadow-2xl"
    : "w-full max-w-md rounded-2xl border border-amber-500/40 bg-slate-900 shadow-2xl shadow-black/30";

  return (
    <div
      className="fixed inset-0 z-[95] flex items-center justify-center bg-black/55 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="type-change-confirm-title"
      onClick={onCancel}
    >
      <div className={`${shell} px-5 py-4`} onClick={(e) => e.stopPropagation()}>
        <h3
          id="type-change-confirm-title"
          className={
            isInlineLight
              ? "text-sm font-semibold text-slate-900"
              : "text-sm font-semibold text-slate-50"
          }
        >
          Change &ldquo;{entityName}&rdquo; to {newTypeLabel}?
        </h3>
        <p
          className={
            isInlineLight
              ? "mt-2 text-sm text-slate-600 leading-relaxed"
              : "mt-2 text-sm text-slate-300 leading-relaxed"
          }
        >
          Related review cards will be updated to match this type.
        </p>

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className={
              isInlineLight
                ? "rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700"
                : "rounded-lg border border-slate-600 px-3 py-1.5 text-xs font-medium text-slate-200"
            }
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-700"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}

export { EntityTypeChangeConfirmDialog };

function GapCatalogTypeField({
  preview,
  reviewEntity,
  surfaceName,
  entityType,
  endLabel,
  isInlineLight,
  renderRosterTypeSelect,
  disabled,
  focusRelIdx,
  row = null,
}) {
  const surface = String(surfaceName || "").trim();
  const type = String(entityType || "application").trim();
  if (
    !surface ||
    !type ||
    !renderRosterTypeSelect ||
    isEnrichmentAnchorEntity(preview, reviewEntity, { surfaceName: surface, entityType: type })
  ) {
    return null;
  }
  const rosterKey = rosterTypeOverrideKeyForGap(preview, surface, type, reviewEntity);
  if (!rosterKey) return null;
  const overridesMap = preview?.enrichment_review_outcomes?.entity_type_overrides || {};
  const effectiveType = overridesMap[rosterKey] ?? type;
  const alignmentHint = catalogTypeAlignmentHint(preview, surface, effectiveType, row);

  return (
    <div className="mt-2 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className={isInlineLight ? "text-xs text-slate-600" : "text-xs text-slate-400"}>
          {endLabel ? `Catalog type (${endLabel}) for` : "Catalog type for"}{" "}
          <span className={isInlineLight ? "font-medium text-slate-900" : "font-medium text-slate-200"}>
            {surface}
          </span>
          :
        </span>
        {renderRosterTypeSelect(rosterKey, type, disabled, isInlineLight, focusRelIdx, surface)}
        {!disabled ? (
          <span className={isInlineLight ? "text-[11px] text-slate-500" : "text-[11px] text-slate-500"}>
            Related suggestions update when you change the type.
          </span>
        ) : null}
      </div>
      <CatalogTypeAlignmentNote hint={alignmentHint} isInlineLight={isInlineLight} />
    </div>
  );
}

function ClaimTitle({ isInlineLight, displayLabel }) {
  return (
    <p
      className={
        isInlineLight
          ? "text-lg font-semibold leading-snug text-slate-900 tracking-tight"
          : "text-lg font-semibold leading-snug text-slate-50 tracking-tight"
      }
    >
      {displayLabel}
    </p>
  );
}

function EditableEntityNameRow({
  entity,
  isInlineLight,
  disabled,
  onSave,
}) {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(entity.name);

  React.useEffect(() => {
    if (!editing) setDraft(entity.name);
  }, [entity.name, editing]);

  if (editing) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <span className={isInlineLight ? "text-xs text-slate-500" : "text-xs text-slate-400"}>
          {entity.label}:
        </span>
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          className={
            isInlineLight
              ? "flex-1 min-w-[12rem] rounded-lg border border-slate-300 px-2 py-1 text-sm text-slate-900"
              : "flex-1 min-w-[12rem] rounded-lg border border-slate-600 bg-slate-900 px-2 py-1 text-sm text-slate-100"
          }
          autoFocus
        />
        <button
          type="button"
          disabled={disabled || !String(draft || "").trim()}
          onClick={() => {
            onSave?.(entity.key, draft);
            setEditing(false);
          }}
          className={
            isInlineLight
              ? "px-2 py-1 rounded-lg bg-indigo-600 text-white text-xs disabled:opacity-40"
              : "px-2 py-1 rounded-lg bg-indigo-700 text-white text-xs disabled:opacity-40"
          }
        >
          Save
        </button>
        <button
          type="button"
          onClick={() => {
            setDraft(entity.name);
            setEditing(false);
          }}
          className={
            isInlineLight
              ? "px-2 py-1 rounded-lg border border-slate-300 text-xs text-slate-700"
              : "px-2 py-1 rounded-lg border border-slate-600 text-xs text-slate-300"
          }
        >
          Cancel
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className={isInlineLight ? "text-xs text-slate-500" : "text-xs text-slate-400"}>
        {entity.label}:
      </span>
      <span className={isInlineLight ? "text-sm font-medium text-slate-900" : "text-sm font-medium text-slate-100"}>
        {entity.name}
      </span>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setEditing(true)}
        title={`Edit ${entity.label} name`}
        className={
          isInlineLight
            ? "p-1 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40"
            : "p-1 rounded-lg border border-slate-600 text-slate-300 hover:bg-slate-800 disabled:opacity-40"
        }
        aria-label={`Edit ${entity.label} name`}
      >
        <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 013.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
          />
        </svg>
      </button>
    </div>
  );
}

function CompoundEntityNameNote({ name, isInlineLight, className = "" }) {
  const message = compoundEntityNameWarning(name);
  if (!message) return null;
  return (
    <p
      className={
        isInlineLight
          ? `text-[11px] leading-relaxed text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5 ${className}`.trim()
          : `text-[11px] leading-relaxed text-amber-100 bg-amber-950/40 border border-amber-600/40 rounded-md px-2 py-1.5 ${className}`.trim()
      }
    >
      {message}
    </p>
  );
}

function EditableEntityNames({ entities, isInlineLight, disabled, onRenameEntity }) {
  if (!Array.isArray(entities) || !entities.length) return null;
  return (
    <div
      className={
        isInlineLight
          ? "rounded-lg border border-slate-200/90 bg-slate-50/80 px-3 py-2 space-y-1.5"
          : "rounded-lg border border-slate-700/80 bg-slate-900/35 px-3 py-2 space-y-1.5"
      }
    >
      {entities.map((entity) => (
        <React.Fragment key={entity.key}>
          <EditableEntityNameRow
            entity={entity}
            isInlineLight={isInlineLight}
            disabled={disabled}
            onSave={onRenameEntity}
          />
          <CompoundEntityNameNote name={entity.name} isInlineLight={isInlineLight} />
        </React.Fragment>
      ))}
    </div>
  );
}

function ReviewCard({
  item,
  isInlineLight,
  actionState,
  actionError,
  displayLabel,
  onApprove,
  onCreateNew,
  onDiscard,
  onUseCandidate,
  onOpenEntityPicker,
  onKeepNew,
  onRenameEntity,
  renderRosterTypeSelect,
  preview,
  reviewEntity,
  matchActions = {},
  anchorName = "",
  reviewSaveLocked = false,
  globalSaveBannerActive = false,
  saveStatusMessage = "",
  closingReview = false,
  /** When false, Add to map / Skip render in the panel wizard footer. */
  showDecisionActions = true,
  highlightAddToMap = false,
  addToMapButtonRef = null,
}) {
  const busy = actionState === "approving" || actionState === "creating";
  const terminal =
    actionState === "approved" ||
    actionState === "discarded" ||
    actionState === "created";

  const labels = getReviewCardActionLabels(item);
  const addLabel = labels.addLabel || labels.primary;
  const linkLabel = labels.linkLabel || labels.secondary || null;

  const relEff = item.type === "relationship" ? augmentRelMissingEndpoint(item.row) : null;
  const catalogRow = item.type === "catalog" ? item.row : null;
  const hasExistingInterface =
    item.type === "catalog" &&
    Boolean(String(catalogRow?.existing_interface_id || "").trim());
  const onlyAdd = Boolean(addLabel) && !linkLabel;
  const addDisabled =
    busy ||
    terminal ||
    reviewSaveLocked ||
    (onlyAdd && item.blocked && !reviewPrimaryActionAllowedWhenBlocked(item));
  const linkDisabled = busy || terminal || reviewSaveLocked;

  const relRosterKey =
    relEff?.missing_surface_name && relEff?.missing_entity_type
      ? rosterTypeOverrideKeyForGap(
          preview,
          relEff?.missing_surface_name,
          relEff?.missing_entity_type,
          reviewEntity
        )
      : "";
  const relOverridesMap = preview?.enrichment_review_outcomes?.entity_type_overrides || {};
  const effectiveMissingType = relRosterKey
    ? relOverridesMap[relRosterKey] ?? relEff?.missing_entity_type
    : relEff?.missing_entity_type;
  const blockedRelCandidates =
    relEff?.missing_surface_name && effectiveMissingType
      ? candidateMatchesForSurface(
          preview,
          relEff?.candidate_matches,
          relEff?.missing_surface_name,
          effectiveMissingType
        )
      : [];

  const handleAddToMap = () => {
    // When Link is also offered, Add always means create-new / force create.
    if (linkLabel && onCreateNew) {
      onCreateNew();
      return;
    }
    onApprove?.();
  };

  const handleLinkExisting = () => {
    if (hasExistingInterface) {
      onApprove?.();
      return;
    }
    if (
      item.type === "relationship" &&
      relEff?.missing_surface_name &&
      effectiveMissingType
    ) {
      onOpenEntityPicker?.({
        surfaceName: relEff.missing_surface_name,
        entityType: effectiveMissingType,
        title: `Link to existing ${effectiveMissingType} for ${relEff.missing_surface_name}`,
        source: "relationship_gap",
        relationshipHint: String(
          item.row?.relationship_type || item.row?.kind || ""
        ).replace(/_/g, " "),
      });
      return;
    }
    if (item.type === "catalog" && catalogRow) {
      const prov = catalogRow.provider_application_name;
      const cons = catalogRow.consumer_application_name;
      const provMissing = !String(catalogRow?.provider_entity_id || "").trim();
      const consMissing = !String(catalogRow?.consumer_entity_id || "").trim();
      if (provMissing && prov) {
        const et = effectiveCatalogSurfaceType(
          preview,
          prov,
          catalogEndpointSuggestedType(catalogRow, "provider"),
          reviewEntity
        );
        onOpenEntityPicker?.({
          surfaceName: prov,
          entityType: et,
          title: `Link to existing ${et} for ${prov}`,
          source: "catalog_provider_gap",
          relationshipHint: String(
            catalogRow?.suggested_name || catalogRow?.interface_name || "data flow"
          ).replace(/_/g, " "),
        });
        return;
      }
      if (consMissing && cons) {
        const et = effectiveCatalogSurfaceType(
          preview,
          cons,
          catalogEndpointSuggestedType(catalogRow, "consumer"),
          reviewEntity
        );
        onOpenEntityPicker?.({
          surfaceName: cons,
          entityType: et,
          title: `Link to existing ${et} for ${cons}`,
          source: "catalog_consumer_gap",
          relationshipHint: String(
            catalogRow?.suggested_name || catalogRow?.interface_name || "data flow"
          ).replace(/_/g, " "),
        });
        return;
      }
    }
    onApprove?.();
  };

  const liveRelationshipClaim = useMemo(() => {
    if (item.type !== "relationship" || item.idx == null || item.idx < 0) return null;
    const row = preview?.proposed_relationships?.[item.idx];
    if (!row) return null;
    const anchor =
      String(anchorName || preview?.anchor_name || "Application").trim() || "Application";
    return compactRelationshipClaim(anchor, row);
  }, [
    item.type,
    item.idx,
    preview?.proposed_relationships,
    anchorName,
    preview?.anchor_name,
  ]);

  const interfaceEnds = useMemo(() => {
    if (item.type !== "catalog" || !catalogRow) return null;
    return interfaceCatalogEndpoints(catalogRow, preview, reviewEntity);
  }, [item.type, catalogRow, preview, reviewEntity]);

  const endpointTypeParts = useMemo(() => {
    // Interface cards use InterfaceEndpointsSummary instead of the footnote.
    if (interfaceEnds?.source || interfaceEnds?.target) return [];
    if (item.type === "relationship" && item.row) {
      return relationshipEndpointTypeParts(
        anchorName || preview?.anchor_name,
        item.row,
        preview,
        reviewEntity
      );
    }
    if (item.type === "catalog" && catalogRow) {
      return catalogEndpointTypeParts(catalogRow, preview, reviewEntity);
    }
    return [];
  }, [
    interfaceEnds,
    item.type,
    item.row,
    catalogRow,
    preview,
    reviewEntity,
    anchorName,
    preview?.anchor_name,
  ]);

  const editableEntities = useMemo(() => {
    const all = reviewItemEditableEntities(item, preview, reviewEntity);
    // Source/Target are shown in InterfaceEndpointsSummary; keep rename for the interface name.
    if (interfaceEnds?.source || interfaceEnds?.target) {
      return all.filter((e) => e.key === "interface");
    }
    return all;
  }, [item, preview, reviewEntity, interfaceEnds]);

  const shellVariant = item.blocked
    ? "blocked"
    : terminal && actionState === "approved"
      ? "approved"
      : busy
        ? "busy"
        : "default";

  const isCatalogRecommendation = Boolean(item.isCatalogRecommendation);

  return (
    <article
      className={`transition-colors ${reviewCardShellClass(isInlineLight, shellVariant, {
        isCatalogRecommendation,
      })} px-4 py-3.5 sm:px-5`}
    >
      <header className="flex items-start justify-between gap-3 mb-2.5">
        <span
          className={reviewKindBadgeClass(isInlineLight, { isCatalogRecommendation })}
        >
          {item.kindLabel}
        </span>
        {busy ? (
          <span
            className={
              isInlineLight
                ? "inline-flex items-center gap-1.5 text-xs font-medium text-indigo-700"
                : "inline-flex items-center gap-1.5 text-xs font-medium text-indigo-300"
            }
          >
            <span
              className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent"
              aria-hidden
            />
            Saving
          </span>
        ) : null}
        {!busy && terminal ? (
          <span
            className={
              actionState === "approved"
                ? isInlineLight
                  ? "text-xs font-medium text-emerald-700"
                  : "text-xs font-medium text-emerald-300"
                : isInlineLight
                  ? "text-xs font-medium text-slate-500"
                  : "text-xs font-medium text-slate-400"
            }
          >
            {actionState === "approved" ? "Added" : "Skipped"}
          </span>
        ) : null}
      </header>

      <div className="space-y-3">
      <ClaimTitle
        isInlineLight={isInlineLight}
        displayLabel={liveRelationshipClaim || displayLabel || item.claim}
      />
      {isCatalogRecommendation ? (
        <p
          className={
            isInlineLight
              ? "text-[11px] leading-relaxed text-teal-800 bg-teal-50 border border-teal-200 rounded-md px-2 py-1.5"
              : "text-[11px] leading-relaxed text-teal-100 bg-teal-950/40 border border-teal-600/40 rounded-md px-2 py-1.5"
          }
        >
          Similar entity in the catalog — not found in linked knowledge.
        </p>
      ) : null}
      {item.type === "catalog" && catalogRow && (interfaceEnds?.source || interfaceEnds?.target) ? (
        <InterfaceEndpointsSummary
          row={catalogRow}
          preview={preview}
          reviewEntity={reviewEntity}
          isInlineLight={isInlineLight}
        />
      ) : null}
      <EditableEntityNames
        entities={editableEntities}
        isInlineLight={isInlineLight}
        disabled={busy || terminal}
        onRenameEntity={(entityKey, newName) => onRenameEntity?.(item, entityKey, newName)}
      />
      <EndpointTypeFootnote parts={endpointTypeParts} isInlineLight={isInlineLight} />
      <EnrichmentContextNotes
        row={item.row}
        anchorName={anchorName}
        isInlineLight={isInlineLight}
        variant="compact"
        previewCatalogOnCreate={Boolean(
          relEff?.missing_end &&
            String(relEff?.missing_surface_name || "").trim() &&
            String(relEff?.missing_entity_type || "").trim()
        )}
        createEntityType={relEff?.missing_entity_type}
        createEntityName={relEff?.missing_surface_name}
      />

      {item.type === "relationship" && item.idx != null ? (
        <MetamodelAdjustmentNote
          preview={preview}
          proposedKey={`p${item.idx}`}
          isInlineLight={isInlineLight}
        />
      ) : null}

      {relEff?.missing_end &&
      String(relEff?.missing_surface_name || "").trim() &&
      String(relEff?.missing_entity_type || "").trim() ? (
        <GapCatalogTypeField
          preview={preview}
          reviewEntity={reviewEntity}
          surfaceName={relEff.missing_surface_name}
          entityType={relEff.missing_entity_type}
          endLabel={relEff.missing_end}
          isInlineLight={isInlineLight}
          renderRosterTypeSelect={renderRosterTypeSelect}
          disabled={busy || terminal}
          focusRelIdx={item.type === "relationship" ? item.idx : null}
          row={relEff}
        />
      ) : null}

      {item.type === "description" && item.row?.proposed_description ? (
        <p
          className={
            isInlineLight
              ? "text-sm text-slate-700 mt-3 whitespace-pre-wrap leading-relaxed"
              : "text-sm text-slate-300 mt-3 whitespace-pre-wrap leading-relaxed"
          }
        >
          {item.row.proposed_description}
        </p>
      ) : null}

      {item.type === "description" && !terminal && !busy && !globalSaveBannerActive ? (
        <p
          className={
            isInlineLight
              ? "text-xs text-slate-500 mt-2 leading-relaxed"
              : "text-xs text-slate-400 mt-2 leading-relaxed"
          }
        >
          {ENRICHMENT_DESCRIPTION_SAVE_HINT}
        </p>
      ) : null}

      {item.blockedMessage ? (
        <div
          className={
            item.blocked
              ? isInlineLight
                ? "mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2"
                : "mt-3 rounded-lg border border-amber-600/40 bg-amber-950/40 px-3 py-2"
              : isInlineLight
                ? "mt-3 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2"
                : "mt-3 rounded-lg border border-sky-600/40 bg-sky-950/40 px-3 py-2"
          }
        >
          <p
            className={
              item.blocked
                ? isInlineLight
                  ? "text-xs text-amber-900"
                  : "text-xs text-amber-100"
                : isInlineLight
                  ? "text-xs text-sky-900"
                  : "text-xs text-sky-100"
            }
          >
            {item.blockedMessage}
          </p>
          {item.blocked &&
          relEff?.missing_surface_name &&
          relEff?.missing_entity_type ? (
            <div className="mt-2 flex flex-wrap gap-2">
              {blockedRelCandidates.slice(0, 3).map((cand) => (
                <button
                  key={cand.id}
                  type="button"
                  onClick={() =>
                    onUseCandidate(cand, relEff.missing_surface_name, effectiveMissingType)
                  }
                  className={
                    isInlineLight
                      ? "px-2 py-1 rounded bg-indigo-600 text-white text-xs"
                      : "px-2 py-1 rounded bg-indigo-700 text-white text-xs"
                  }
                >
                  Link to existing — {candidateActionLabel(cand)}
                </button>
              ))}
              <button
                type="button"
                onClick={() =>
                  onOpenEntityPicker?.({
                    surfaceName: relEff?.missing_surface_name,
                    entityType: effectiveMissingType,
                    title: `Link to existing ${effectiveMissingType} for ${relEff?.missing_surface_name}`,
                    source: "relationship_gap",
                    relationshipHint: String(item.row?.relationship_type || item.row?.kind || "").replace(/_/g, " "),
                  })
                }
                className={
                  isInlineLight
                    ? "px-2 py-1 rounded border border-indigo-300 text-xs text-indigo-800"
                    : "px-2 py-1 rounded border border-indigo-500/45 text-xs text-indigo-200"
                }
              >
                Link to existing…
              </button>
            </div>
          ) : null}
          {item.blocked &&
          catalogRow &&
          !String(catalogRow.existing_interface_id || "").trim() ? (
            <CatalogEndpointHints
              row={catalogRow}
              preview={preview}
              reviewEntity={reviewEntity}
              isInlineLight={isInlineLight}
              renderRosterTypeSelect={renderRosterTypeSelect}
              onUseCandidate={onUseCandidate}
              onOpenEntityPicker={onOpenEntityPicker}
            />
          ) : null}
        </div>
      ) : null}

      {actionError ? <p className="text-xs text-rose-400 mt-2">{actionError}</p> : null}

      {labels.hint ? (
        <p
          className={
            isInlineLight
              ? "text-xs leading-relaxed text-slate-600"
              : "text-xs leading-relaxed text-slate-400"
          }
        >
          {labels.hint}
        </p>
      ) : null}
      </div>

      {showDecisionActions ? (
        <ReviewDecisionFooter
          isInlineLight={isInlineLight}
          addLabel={addLabel}
          linkLabel={linkLabel}
          discardLabel={labels.discard}
          busy={busy}
          busyLabel={labels.busy}
          addDisabled={addDisabled}
          linkDisabled={linkDisabled}
          discardDisabled={busy || terminal}
          onAdd={handleAddToMap}
          onLink={handleLinkExisting}
          onDiscard={onDiscard}
          addTitle={
            item.blocked
              ? "Match both systems on your map first, or choose Add to map"
              : undefined
          }
          addHint={labels.primaryHint || null}
          linkHint={labels.secondaryHint || null}
          discardHint={labels.discardHint || null}
          highlightAdd={highlightAddToMap}
          addButtonRef={addToMapButtonRef}
          extra={
            <SavingToCatalogBanner
              message={saveStatusMessage}
              isInlineLight={isInlineLight}
              closing={closingReview}
              variant="inline"
            />
          }
        />
      ) : null}
    </article>
  );
}

function DecidedReviewCard({
  item,
  isInlineLight,
  anchorName = "",
  onRevise,
  reviseDisabled = false,
}) {
  const approved = item.decision === "approved";
  const border = approved
    ? isInlineLight
      ? "border-emerald-200 bg-emerald-50/60"
      : "border-emerald-600/40 bg-emerald-950/25"
    : isInlineLight
      ? "border-slate-200 bg-slate-50"
      : "border-slate-700/70 bg-slate-900/35";

  const badge = approved
    ? isInlineLight
      ? "bg-emerald-100 text-emerald-800 border-emerald-200"
      : "bg-emerald-900/50 text-emerald-200 border-emerald-700/50"
    : isInlineLight
      ? "bg-slate-200 text-slate-700 border-slate-300"
      : "bg-slate-800 text-slate-300 border-slate-600";

  return (
    <li className={`rounded-lg border px-3 py-2.5 ${border}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p
            className={
              isInlineLight
                ? "text-[10px] uppercase tracking-wide text-slate-500"
                : "text-[10px] uppercase tracking-wide text-slate-400"
            }
          >
            {item.kindLabel}
          </p>
          <p
            className={
              isInlineLight
                ? "text-sm font-medium text-slate-900 mt-0.5"
                : "text-sm font-medium text-slate-100 mt-0.5"
            }
          >
            {item.claim}
          </p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1.5">
          <span
            className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded border ${badge}`}
          >
            {approved ? "Added" : "Skipped"}
          </span>
          {onRevise ? (
            <button
              type="button"
              disabled={reviseDisabled}
              onClick={() => onRevise(item)}
              className={
                isInlineLight
                  ? "text-xs font-medium text-indigo-700 hover:text-indigo-900 underline disabled:opacity-40 disabled:no-underline"
                  : "text-xs font-medium text-indigo-300 hover:text-indigo-100 underline disabled:opacity-40 disabled:no-underline"
              }
            >
              Change decision
            </button>
          ) : null}
        </div>
      </div>
      <EnrichmentContextNotes
        row={item.row}
        anchorName={anchorName}
        isInlineLight={isInlineLight}
        variant="compact"
        previewCatalogOnCreate={
          item.type === "relationship" &&
          Boolean(
            augmentRelMissingEndpoint(item.row)?.missing_end &&
              String(item.row?.missing_surface_name || "").trim()
          )
        }
        createEntityType={item.row?.missing_entity_type}
        createEntityName={item.row?.missing_surface_name}
      />
      {item.type === "description" && item.row?.proposed_description ? (
        <p
          className={
            isInlineLight
              ? "text-xs text-slate-600 mt-2 line-clamp-3 whitespace-pre-wrap"
              : "text-xs text-slate-400 mt-2 line-clamp-3 whitespace-pre-wrap"
          }
        >
          {item.row.proposed_description}
        </p>
      ) : null}
    </li>
  );
}

function DecidedItemsSection({
  items,
  isInlineLight,
  anchorName = "",
  defaultExpanded = false,
  onReviseDecided,
  reviseDisabled = false,
  /** When false, list scrolls with the parent panel (avoids nested scrollbars). */
  constrainListHeight = false,
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [decisionFilter, setDecisionFilter] = useState("all");

  const approvedCount = useMemo(
    () => items.filter((it) => it.decision === "approved").length,
    [items]
  );
  const discardedCount = useMemo(
    () => items.filter((it) => it.decision === "discarded").length,
    [items]
  );
  const filteredItems = useMemo(() => {
    if (decisionFilter === "approved") {
      return items.filter((it) => it.decision === "approved");
    }
    if (decisionFilter === "discarded") {
      return items.filter((it) => it.decision === "discarded");
    }
    return items;
  }, [items, decisionFilter]);

  if (!items.length) return null;

  const shell = isInlineLight
    ? "rounded-lg border border-slate-200 bg-slate-50/80"
    : "rounded-lg border border-slate-700/80 bg-slate-900/30";

  const chipClass = (active) =>
    active
      ? isInlineLight
        ? "px-2 py-1 rounded text-xs bg-indigo-100 text-indigo-900 font-medium"
        : "px-2 py-1 rounded text-xs bg-cyan-900/50 text-cyan-100 font-medium"
      : isInlineLight
        ? "px-2 py-1 rounded text-xs text-slate-600"
        : "px-2 py-1 rounded text-xs text-slate-400";

  return (
    <section className={shell}>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className={
          isInlineLight
            ? "w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left text-sm font-semibold text-slate-800"
            : "w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left text-sm font-semibold text-slate-100"
        }
        aria-expanded={expanded}
      >
        <span>Your choices ({items.length})</span>
        <span
          className={
            isInlineLight ? "text-slate-500 font-normal" : "text-slate-400 font-normal"
          }
          aria-hidden
        >
          {expanded ? "▾" : "▸"}
        </span>
      </button>
      {expanded ? (
        <div className="border-t border-inherit">
          <div className="px-3 pt-2 pb-1 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setDecisionFilter("all")}
              className={chipClass(decisionFilter === "all")}
            >
              All ({items.length})
            </button>
            <button
              type="button"
              onClick={() => setDecisionFilter("approved")}
              className={chipClass(decisionFilter === "approved")}
            >
              Added ({approvedCount})
            </button>
            <button
              type="button"
              onClick={() => setDecisionFilter("discarded")}
              className={chipClass(decisionFilter === "discarded")}
            >
              Skipped ({discardedCount})
            </button>
          </div>
          {filteredItems.length ? (
            <ul
              className={
                constrainListHeight
                  ? "px-3 pb-2 space-y-2 max-h-72 overflow-y-auto"
                  : "px-3 pb-2 space-y-2"
              }
            >
              {filteredItems.map((it) => (
                <DecidedReviewCard
                  key={it.id}
                  item={it}
                  isInlineLight={isInlineLight}
                  anchorName={anchorName}
                  onRevise={onReviseDecided}
                  reviseDisabled={reviseDisabled}
                />
              ))}
            </ul>
          ) : (
            <p
              className={
                isInlineLight
                  ? "px-3 pb-3 text-xs text-slate-500"
                  : "px-3 pb-3 text-xs text-slate-400"
              }
            >
              {decisionFilter === "approved"
                ? "No added items."
                : decisionFilter === "discarded"
                  ? "No skipped items."
                  : "No items."}
            </p>
          )}
        </div>
      ) : null}
    </section>
  );
}

function SavingToCatalogBanner({
  message,
  isInlineLight,
  closing = false,
  variant = "banner",
}) {
  if (!message && !closing) return null;
  const text = closing
    ? "Finishing saves before you leave…"
    : message;

  if (variant === "inline") {
    return (
      <span
        className={
          isInlineLight
            ? "inline-flex items-center gap-2 text-xs text-indigo-800 ml-1 max-w-md"
            : "inline-flex items-center gap-2 text-xs text-indigo-200 ml-1 max-w-md"
        }
        role="status"
        aria-live="polite"
      >
        <span
          className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent shrink-0"
          aria-hidden
        />
        <span>{text}</span>
      </span>
    );
  }

  return (
    <div
      className={
        isInlineLight
          ? "rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-2.5 text-sm text-indigo-950"
          : "rounded-lg border border-indigo-500/40 bg-indigo-950/40 px-4 py-2.5 text-sm text-indigo-100"
      }
      role="status"
      aria-live="polite"
    >
      <div className="flex items-center gap-3">
        <span
          className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent shrink-0"
          aria-hidden
        />
        <p className={isInlineLight ? "text-sm text-indigo-900" : "text-sm text-indigo-100"}>
          {text}
        </p>
      </div>
    </div>
  );
}

function DoneView({
  stats,
  isInlineLight,
  anchorName = "",
  descriptionOffer,
  onDescription,
  descBusy,
  descriptionApplied,
  decidedItems = [],
  onReviseDecided,
  reviewSaveLocked = false,
  saveStatusMessage = "",
  reviewCompleteEarly = false,
  onContinueReview,
}) {
  const skipped = Math.max(0, Number(stats?.pending) || 0);
  return (
    <div className="space-y-4">
      <SavingToCatalogBanner message={saveStatusMessage} isInlineLight={isInlineLight} />
      <div
        className={
          isInlineLight
            ? "text-center py-4 px-4 rounded-lg border border-slate-200 bg-slate-50/90"
            : "text-center py-4 px-4 rounded-lg border border-slate-700/80 bg-slate-900/30"
        }
      >
        <h3
          className={
            isInlineLight ? "text-lg font-semibold text-slate-900" : "text-lg font-semibold text-slate-100"
          }
        >
          {reviewCompleteEarly ? "Review marked complete" : "All suggestions reviewed"}
        </h3>
        <p className={isInlineLight ? "text-sm text-slate-600 mt-2" : "text-sm text-slate-400 mt-2"}>
          {stats.approved} approved · {stats.discarded} discarded
          {skipped > 0 ? ` · ${skipped} skipped` : ""}
        </p>
        {reviewCompleteEarly && skipped > 0 ? (
          <p
            className={
              isInlineLight
                ? "text-xs text-slate-500 mt-2 max-w-md mx-auto leading-relaxed"
                : "text-xs text-slate-400 mt-2 max-w-md mx-auto leading-relaxed"
            }
          >
            Items you did not decide stay on this upload but it no longer shows as needing your OK.
            You can keep reviewing below if you change your mind.
          </p>
        ) : null}
        {onContinueReview && skipped > 0 ? (
          <button
            type="button"
            disabled={reviewSaveLocked}
            onClick={onContinueReview}
            className={
              isInlineLight
                ? "mt-4 px-4 py-2 rounded-lg border border-indigo-300 text-sm font-medium text-indigo-800 hover:bg-indigo-50 disabled:opacity-50"
                : "mt-4 px-4 py-2 rounded-lg border border-indigo-500/50 text-sm font-medium text-indigo-200 hover:bg-indigo-950/40 disabled:opacity-50"
            }
          >
            Continue reviewing skipped items
          </button>
        ) : null}
        {descriptionOffer && !descriptionApplied ? (
          <button
            type="button"
            disabled={descBusy || reviewSaveLocked}
            onClick={onDescription}
            className={
              isInlineLight
                ? "mt-4 px-4 py-2 rounded-lg border border-slate-300 text-sm text-slate-800"
                : "mt-4 px-4 py-2 rounded-lg border border-slate-600 text-sm text-slate-200"
            }
          >
            {descBusy ? "Saving…" : "Update app description"}
          </button>
        ) : null}
      </div>
      <DecidedItemsSection
        items={decidedItems}
        isInlineLight={isInlineLight}
        anchorName={anchorName}
        defaultExpanded
        onReviseDecided={onReviseDecided}
        reviseDisabled={reviewSaveLocked}
        constrainListHeight={false}
      />
    </div>
  );
}

export default function EnrichmentReviewQueuePanel({
  isInlineLight,
  anchorSummary,
  reviewEntity = null,
  queue,
  decidedItems = [],
  stats,
  setupNeeded,
  setupComplete,
  potentialMatches,
  matchActions,
  preview,
  relActions,
  catalogActions,
  descProposal,
  descriptionApplied,
  descBusy,
  descError = "",
  onConfirmMatch,
  onKeepNew,
  onDiscardMatch,
  onApproveItem,
  onCreateNewItem,
  onDiscardItem,
  onUseCandidate,
  onOpenEntityPicker,
  onAcceptDescription,
  onReviseDecided,
  onRenameItem,
  renderRosterTypeSelect,
  metamodelSyncNotice = null,
  onDismissMetamodelSyncNotice,
  reviewSaveLocked: _reviewSaveLocked = false,
  reviewGlobalLocked = false,
  catalogSavesBusy = false,
  saveStatusMessage = "",
  closingReview = false,
  reviewComplete = false,
  onMarkReviewComplete,
  onResumeReview,
  onBulkAcceptCatalogMatches,
  bulkBusy = false,
}) {
  // Background catalog saves must not freeze the steward on the current card —
  // only closing / global lock should block navigation. Link to existing stays
  // clickable while previous cards finish writing to the map.
  const stewardActionsLocked = Boolean(closingReview || reviewGlobalLocked);
  const anchorName = useMemo(
    () => String(reviewEntity?.name || preview?.anchor_name || "").trim(),
    [reviewEntity?.name, preview?.anchor_name]
  );
  const productGuide = useOptionalProductGuide();
  const guideAddToMapRef = useRef(null);
  const [phase, setPhase] = useState(() =>
    resolveInitialReviewPhase({
      reviewComplete,
      queueLength: queue.length,
    })
  );
  const [index, setIndex] = useState(0);
  const [reviewFilter, setReviewFilter] = useState("all");
  const focusIdRef = useRef(null);
  const sessionKeyRef = useRef("");
  const guideHighlightAddToMap = Boolean(
    productGuide?.active &&
      productGuide?.chapter?.id === "review" &&
      phase === "review"
  );

  const sessionKey = String(
    preview?.session_id || preview?.anchor_application_id || ""
  ).trim();

  /** New enrichment session only — not on every preview patch after save. */
  useEffect(() => {
    if (!sessionKey) return;
    if (sessionKey !== sessionKeyRef.current) {
      sessionKeyRef.current = sessionKey;
      setIndex(0);
      focusIdRef.current = null;
      if (!reviewComplete && queue.length > 0) {
        setPhase("review");
      }
    }
  }, [sessionKey, reviewComplete, queue.length]);

  const queueCounts = useMemo(() => countReviewQueueNewFindings(queue), [queue]);
  const catalogMatchCount = useMemo(
    () => filterAcceptableAiRecommendationItems(queue).length,
    [queue]
  );

  const overviewContent = useMemo(
    () => formatReviewOverviewContent(queueCounts, { decidedCount: stats.reviewed || 0 }),
    [queueCounts, stats.reviewed]
  );

  const findingsSummary = useMemo(
    () => formatReviewQueueFindingsSummary(queueCounts),
    [queueCounts]
  );

  const startReview = useCallback((filterMode) => {
    setReviewFilter(filterMode === "new" ? "new" : "all");
    setIndex(0);
    focusIdRef.current = null;
    setPhase("review");
  }, []);

  useEffect(() => {
    if (reviewFilter === "new" && queueCounts.actionableCount === 0 && queue.length > 0) {
      setReviewFilter("all");
    }
  }, [reviewFilter, queueCounts.actionableCount, queue.length]);

  const handleReviewFilterChange = useCallback((mode) => {
    setReviewFilter(mode === "new" ? "new" : "all");
    focusIdRef.current = null;
    setIndex(0);
  }, []);

  const getActionState = useCallback(
    (it) => {
      if (!it) return "";
      if (it.type === "name_match") {
        const rk = it.resolutionKey || resolutionKey(it.row);
        const act = setupMatchAction(preview, rk, matchActions);
        if (act.state === "confirming") return "approving";
        if (act.state === "creating") return "creating";
        if (act.state === "confirmed" || act.state === "kept_new") return "approved";
        if (act.state === "discarded") return "discarded";
        return "";
      }
      if (it.type === "relationship") {
        return relationshipUiState(preview, it.row, it.idx, relActions)?.state || "";
      }
      if (it.type === "catalog") {
        return catalogUiState(preview, it.row, it.idx, catalogActions)?.state || "";
      }
      if (it.type === "description") {
        if (descBusy) return "approving";
        if (descriptionApplied) return "approved";
        return "";
      }
      return "";
    },
    [preview, relActions, catalogActions, descriptionApplied, descBusy, matchActions]
  );

  const pendingQueue = useMemo(() => {
    const base = filterReviewQueue(queue, reviewFilter);
    // In-flight Add-to-map work leaves the one-at-a-time queue immediately so the
    // next card can be reviewed while creates continue in the background.
    return base.filter((it) => {
      const state = getActionState(it);
      return state !== "creating" && state !== "approving";
    });
  }, [queue, reviewFilter, getActionState]);

  useEffect(() => {
    const id = pendingQueue[index]?.id;
    if (id) focusIdRef.current = id;
  }, [index, pendingQueue]);

  /**
   * When the queue shrinks after a decision, keep index stable so the next item
   * slides into place (or clamp when filtering). Do not jump back to item 0.
   */
  useEffect(() => {
    if (!pendingQueue.length) return;
    const fid = focusIdRef.current;
    if (fid) {
      const at = pendingQueue.findIndex((q) => q.id === fid);
      if (at >= 0) {
        setIndex((i) => (i === at ? i : at));
        return;
      }
    }
    setIndex((i) => {
      const next = Math.min(i, Math.max(0, pendingQueue.length - 1));
      const nextId = pendingQueue[next]?.id;
      if (nextId) focusIdRef.current = nextId;
      return next;
    });
  }, [pendingQueue]);

  const item = pendingQueue[index];
  const itemLabels = item ? getReviewCardActionLabels(item) : null;
  const showGuideAddToMap =
    guideHighlightAddToMap &&
    Boolean(item) &&
    Boolean(itemLabels?.addLabel || itemLabels?.primary);

  // Guided demo: tip follows the card action (Add to map), not “Open Review”.
  useEffect(() => {
    if (!showGuideAddToMap) return;
    emitGuideEvent("wizard-step", {
      step: "review-decide",
      hasAnchor: true,
      hasNotes: false,
    });
  }, [showGuideAddToMap, item?.id]);

  // Guided demo: keep blinking Add to map above the scrollbar fold.
  useLayoutEffect(() => {
    if (!showGuideAddToMap) return undefined;
    const el = guideAddToMapRef.current;
    if (!el) return undefined;
    let inner = 0;
    const outer = window.requestAnimationFrame(() => {
      inner = window.requestAnimationFrame(() => {
        revealGuideAttentionTarget(el);
      });
    });
    return () => {
      window.cancelAnimationFrame(outer);
      if (inner) window.cancelAnimationFrame(inner);
    };
  }, [showGuideAddToMap, item?.id, index, phase]);

  const jumpToNameMatching = useCallback(() => {
    const at = pendingQueue.findIndex((q) => q.type === "name_match");
    if (at < 0) return;
    setPhase("review");
    setIndex(at);
    focusIdRef.current = pendingQueue[at]?.id || null;
  }, [pendingQueue]);

  /** After discard / approve: pending queue drops the card; same index shows the next item. */
  const advanceAfterDecision = useCallback(() => {
    focusIdRef.current = null;
    setIndex((i) => i);
  }, []);

  const handleApproveCurrent = useCallback(() => {
    if (!item || stewardActionsLocked) return;
    const state = getActionState(item);
    if (state === "approving" || state === "creating") return;
    // Clear focus so when this card leaves the pending queue, the next item
    // slides into the same index instead of trying to re-find the saving card.
    focusIdRef.current = null;
    onApproveItem(item);
  }, [item, getActionState, stewardActionsLocked, onApproveItem]);

  const handleDiscardWithAdvance = useCallback(() => {
    if (!item || stewardActionsLocked) return;
    const state = getActionState(item);
    if (state === "approving" || state === "creating") return;
    onDiscardItem(item);
    advanceAfterDecision();
  }, [item, advanceAfterDecision, getActionState, onDiscardItem, stewardActionsLocked]);

  const handleCreateNewCurrent = useCallback(() => {
    if (!item || stewardActionsLocked) return;
    const state = getActionState(item);
    if (state === "approving" || state === "creating") return;
    focusIdRef.current = null;
    onCreateNewItem(item);
  }, [item, getActionState, stewardActionsLocked, onCreateNewItem]);

  const getDisplayLabel = useCallback(
    (it) => {
      if (!it) return "";
      return it.claim;
    },
    []
  );

  const getActionError = useCallback(
    (it) => {
      if (!it) return "";
      if (it.type === "name_match") {
        const rk = it.resolutionKey || resolutionKey(it.row);
        return matchActions[rk]?.error || "";
      }
      if (it.type === "relationship") {
        return relActions[`p${it.idx}`]?.error || "";
      }
      if (it.type === "catalog") {
        return catalogActions[catalogStubKey(it.row, it.idx)]?.error || "";
      }
      if (it.type === "description") {
        return descError || "";
      }
      return "";
    },
    [relActions, catalogActions, descError, matchActions]
  );

  const handleKeepNewMatch = useCallback(
    (row) => {
      onKeepNew(row);
      advanceAfterDecision();
    },
    [onKeepNew, advanceAfterDecision]
  );

  const handleDiscardMatchWithAdvance = useCallback(
    (row) => {
      onDiscardMatch(row);
      advanceAfterDecision();
    },
    [onDiscardMatch, advanceAfterDecision]
  );

  useEffect(() => {
    const savesStillRunning = Boolean(catalogSavesBusy || saveStatusMessage);
    // Don't jump to Done while map writes are still finishing after the last card.
    const finished =
      reviewComplete || (queue.length === 0 && !savesStillRunning);
    if (finished) {
      if (phase === "review" || phase === "overview") {
        setPhase("done");
        emitGuideEvent("review-complete", {
          jobId: String(preview?.job_id || ""),
        });
      }
      return;
    }
    if (phase === "done" && !reviewComplete) {
      setPhase("review");
    }
  }, [
    phase,
    queue.length,
    reviewComplete,
    catalogSavesBusy,
    saveStatusMessage,
    preview?.job_id,
  ]);


  const globalActionsDisabled = stewardActionsLocked;
  // Keep Prev/Next usable during background catalog saves so review can continue.
  const navDisabled = stewardActionsLocked;
  const globalSaveBannerActive = Boolean(saveStatusMessage || closingReview);

  useEffect(() => {
    if (phase !== "review") return undefined;
    const onKey = (e) => {
      if (["INPUT", "TEXTAREA", "SELECT"].includes(e.target.tagName)) return;
      if (globalActionsDisabled) return;
      if (e.key === "ArrowRight") {
        setIndex((i) => Math.min(pendingQueue.length - 1, i + 1));
      } else if (e.key === "ArrowLeft") {
        setIndex((i) => Math.max(0, i - 1));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [phase, pendingQueue.length, globalActionsDisabled]);

  if (phase === "done") {
    return (
      <DoneView
        stats={stats}
        isInlineLight={isInlineLight}
        anchorName={anchorName}
        descriptionOffer={Boolean(descProposal?.proposed_description)}
        onDescription={onAcceptDescription}
        descBusy={descBusy}
        descriptionApplied={descriptionApplied}
        decidedItems={decidedItems}
        onReviseDecided={onReviseDecided}
        reviewSaveLocked={globalActionsDisabled}
        saveStatusMessage={saveStatusMessage}
        reviewCompleteEarly={reviewComplete && queue.length > 0}
        onContinueReview={
          reviewComplete && queue.length > 0 && onResumeReview ? onResumeReview : undefined
        }
      />
    );
  }

  if (phase === "overview") {
    return (
      <div className="space-y-4">
        <MetamodelSyncBanner
          notice={metamodelSyncNotice}
          isInlineLight={isInlineLight}
          onDismiss={onDismissMetamodelSyncNotice}
        />
        <ReviewOverviewView
          isInlineLight={isInlineLight}
          anchorSummary={anchorSummary}
          counts={queueCounts}
          overviewContent={overviewContent}
          stats={stats}
          decidedItems={decidedItems}
          anchorName={anchorName}
          onStartReviewNew={() => startReview("new")}
          onStartReviewAll={() => startReview("all")}
          onBulkAcceptCatalogMatches={onBulkAcceptCatalogMatches}
          onMarkReviewComplete={onMarkReviewComplete}
          onOpenSetup={jumpToNameMatching}
          setupNeeded={setupNeeded}
          bulkBusy={bulkBusy}
          actionsDisabled={globalActionsDisabled}
          saveStatusMessage={saveStatusMessage}
          closingReview={closingReview}
          reviewComplete={reviewComplete}
          catalogMatchCount={catalogMatchCount}
          onReviseDecided={onReviseDecided}
        />
      </div>
    );
  }

  if (!item) {
    const filteredEmpty = queue.length > 0 && reviewFilter === "new";
    return (
      <div className="space-y-4">
        <MetamodelSyncBanner
          notice={metamodelSyncNotice}
          isInlineLight={isInlineLight}
          onDismiss={onDismissMetamodelSyncNotice}
        />
        <ReviewProgressHeader
          index={0}
          total={0}
          reviewed={stats.reviewed}
          isInlineLight={isInlineLight}
          setupNeeded={setupNeeded}
          onOpenSetup={jumpToNameMatching}
          onOpenOverview={() => setPhase("overview")}
          filterMode={reviewFilter}
          onFilterModeChange={handleReviewFilterChange}
          queueTotal={queueCounts.total}
          newFindingsCount={queueCounts.newCount}
          actionableCount={queueCounts.actionableCount}
          findingsSummary={findingsSummary}
        />
        {filteredEmpty ? (
          <div
            className={
              isInlineLight
                ? "rounded-xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-700"
                : "rounded-xl border border-slate-700 bg-slate-900/40 px-4 py-4 text-sm text-slate-300"
            }
          >
            <p className="font-medium">Nothing here needs a new decision</p>
            <p className="mt-1 text-xs opacity-90">
              {queueCounts.existingMatchCount} item
              {queueCounts.existingMatchCount === 1 ? "" : "s"} already{" "}
              <span className="font-medium">match your map</span>. Switch to{" "}
              <span className="font-medium">All</span> to review them anyway.
            </p>
            <button
              type="button"
              onClick={() => handleReviewFilterChange("all")}
              className={
                isInlineLight
                  ? "mt-3 text-xs font-medium text-indigo-700 hover:text-indigo-900 underline"
                  : "mt-3 text-xs font-medium text-indigo-300 hover:text-indigo-100 underline"
              }
            >
              Show all pending ({queueCounts.total})
            </button>
          </div>
        ) : catalogSavesBusy || saveStatusMessage ? (
          <div className="space-y-3">
            <SavingToCatalogBanner
              message={saveStatusMessage || "Adding to your map in the background…"}
              isInlineLight={isInlineLight}
              closing={closingReview}
            />
            <p
              className={
                isInlineLight ? "text-sm text-slate-600" : "text-sm text-slate-400"
              }
            >
              You have decided every card. Map updates are still finishing — this review
              will complete automatically when they finish.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            <p className={isInlineLight ? "text-sm text-slate-600" : "text-sm text-slate-400"}>
              {reviewComplete
                ? "This review is complete."
                : "No pending items in this review."}
            </p>
          </div>
        )}
        <DecidedItemsSection
          items={decidedItems}
          isInlineLight={isInlineLight}
          anchorName={anchorName}
          defaultExpanded={decidedItems.length > 0 && queue.length === 0}
          onReviseDecided={onReviseDecided}
        />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <MetamodelSyncBanner
        notice={metamodelSyncNotice}
        isInlineLight={isInlineLight}
        onDismiss={onDismissMetamodelSyncNotice}
      />
      {globalSaveBannerActive ? (
        <SavingToCatalogBanner
          message={saveStatusMessage}
          isInlineLight={isInlineLight}
          closing={closingReview}
        />
      ) : null}
      <ReviewProgressHeader
        index={index}
        total={pendingQueue.length}
        reviewed={stats.reviewed}
        isInlineLight={isInlineLight}
        setupNeeded={setupNeeded}
        onOpenSetup={jumpToNameMatching}
        onOpenOverview={() => setPhase("overview")}
        filterMode={reviewFilter}
        onFilterModeChange={handleReviewFilterChange}
        queueTotal={queueCounts.total}
          newFindingsCount={queueCounts.newCount}
          actionableCount={queueCounts.actionableCount}
        findingsSummary={findingsSummary}
      />

      {item?.type === "name_match" ? (
        <NameMatchReviewCard
          key={item.id}
          item={item}
          isInlineLight={isInlineLight}
          actionState={getActionState(item)}
          actionError={getActionError(item)}
          preview={preview}
          reviewEntity={reviewEntity}
          matchActions={matchActions}
          renderRosterTypeSelect={renderRosterTypeSelect}
          onConfirmMatch={onConfirmMatch}
          onOpenEntityPicker={onOpenEntityPicker}
          onKeepNew={handleKeepNewMatch}
          onDiscardMatch={handleDiscardMatchWithAdvance}
          onRenameEntity={onRenameItem}
          // Keep name matching clickable while background catalog creates run.
          reviewSaveLocked={stewardActionsLocked}
          showDecisionActions
          highlightAddToMap={showGuideAddToMap}
          addToMapButtonRef={guideAddToMapRef}
        />
      ) : item ? (
        <ReviewCard
          key={item.id}
          item={item}
          isInlineLight={isInlineLight}
          actionState={getActionState(item)}
          actionError={getActionError(item)}
          displayLabel={getDisplayLabel(item)}
          onApprove={handleApproveCurrent}
          onCreateNew={handleCreateNewCurrent}
          onDiscard={handleDiscardWithAdvance}
          onUseCandidate={onUseCandidate}
          onOpenEntityPicker={onOpenEntityPicker}
          onKeepNew={handleKeepNewMatch}
          onRenameEntity={onRenameItem}
          renderRosterTypeSelect={renderRosterTypeSelect}
          preview={preview}
          reviewEntity={reviewEntity}
          matchActions={matchActions}
          anchorName={anchorName}
          reviewSaveLocked={stewardActionsLocked}
          globalSaveBannerActive={globalSaveBannerActive}
          saveStatusMessage={saveStatusMessage}
          closingReview={closingReview}
          showDecisionActions
          highlightAddToMap={showGuideAddToMap}
          addToMapButtonRef={guideAddToMapRef}
        />
      ) : null}

      <div className={reviewWizardFooterShell(isInlineLight)} role="group" aria-label="Review navigation">
        <div className="flex flex-wrap items-center justify-center gap-2">
          <button
            type="button"
            disabled={index <= 0 || navDisabled}
            onClick={() => setIndex((i) => Math.max(0, i - 1))}
            className={reviewNavBtn(isInlineLight)}
            aria-label="Previous"
            title="Previous"
          >
            <ReviewNavArrow direction="prev" />
          </button>
          {!globalSaveBannerActive && saveStatusMessage ? (
            <SavingToCatalogBanner
              message={saveStatusMessage}
              isInlineLight={isInlineLight}
              closing={closingReview}
              variant="inline"
            />
          ) : null}
          <button
            type="button"
            disabled={index >= pendingQueue.length - 1 || navDisabled}
            onClick={() =>
              setIndex((i) => Math.min(pendingQueue.length - 1, i + 1))
            }
            className={reviewNavBtn(isInlineLight)}
            aria-label="Next"
            title="Next"
          >
            <ReviewNavArrow direction="next" />
          </button>
        </div>
      </div>

      <DecidedItemsSection
        items={decidedItems}
        isInlineLight={isInlineLight}
        anchorName={anchorName}
        defaultExpanded={false}
        onReviseDecided={onReviseDecided}
        reviseDisabled={catalogSavesBusy || globalActionsDisabled}
      />
    </div>
  );
}
